import { toNestErrors, validateFieldsNatively } from '@hookform/resolvers';
import type { FieldError, FieldValues, ResolverOptions, ResolverResult } from 'react-hook-form';
import { z } from 'zod';

import { resolveLazyTranslation } from '../i18n/validation.js';

// Self-contained Zod v4 resolver for react-hook-form.
//
// We do NOT delegate to `@hookform/resolvers/zod` here: the installed
// resolvers build reads the (removed) v3 `error.errors` array, so it rethrows
// instead of converting Zod v4's `error.issues` into react-hook-form errors.
// This wrapper walks `error.issues` directly (handling the v4 `invalid_union`
// nesting) and resolves our lazy i18n markers on each message, so all code
// keeps referencing the same Zod types and translation lifecycle.

type Resolver = <TSchema extends z.ZodTypeAny>(
  schema: TSchema,
  schemaOptions?: Partial<z.core.ParseContext<z.core.$ZodIssue>>,
  factoryOptions?: {
    mode?: 'async' | 'sync';
    raw?: boolean;
  },
) => <TFieldValues extends FieldValues, TContext>(
  values: TFieldValues,
  context: TContext | undefined,
  options: ResolverOptions<TFieldValues>,
) => Promise<ResolverResult<TFieldValues>>;

/**
 * Accumulates every validation message per error type for a field. Mirrors
 * react-hook-form's own `appendErrors`, used only when `criteriaMode: 'all'`.
 */
function appendError(existing: FieldError | undefined, type: string, message: string): FieldError {
  const types = { ...existing?.types };
  const previous = types[type];
  types[type] = previous
    ? ([] as string[]).concat(previous as string | string[], message)
    : message;

  return {
    type: existing?.type ?? type,
    message: existing?.message ?? message,
    types,
    ref: existing?.ref,
  } as FieldError;
}

/**
 * Converts a flat list of Zod v4 issues into the nested error map that
 * react-hook-form expects. Mirrors the upstream resolver algorithm, but reads
 * `issue.errors` (the v4 `invalid_union` shape) instead of v3 `unionErrors`,
 * and resolves lazy translation markers on every message.
 */
function parseIssues(
  issues: z.core.$ZodIssue[],
  validateAllFieldCriteria: boolean,
): Record<string, FieldError> {
  const errors: Record<string, FieldError> = {};

  for (let index = 0; index < issues.length;) {
    const issue = issues[index];
    const { code, path } = issue;
    const dotPath = path.join('.');

    // The first member's first error stands in for the union at this path
    // (matches the upstream resolver's `unionErrors[0].errors[0]` behaviour).
    const unionIssue =
      code === 'invalid_union' ? (issue as z.core.$ZodIssueInvalidUnion).errors[0]?.[0] : undefined;
    const type = unionIssue ? unionIssue.code : code;
    const message = resolveLazyTranslation(unionIssue ? unionIssue.message : issue.message);

    if (!errors[dotPath]) {
      errors[dotPath] = { message, type };
    }

    if (code === 'invalid_union') {
      // Flatten the remaining nested issues so deeper field errors are kept.
      (issue as z.core.$ZodIssueInvalidUnion).errors.forEach((nested) =>
        nested.forEach((nestedIssue) => issues.push(nestedIssue)),
      );
    }

    if (validateAllFieldCriteria) {
      errors[dotPath] = appendError(errors[dotPath], type, message);
    }

    index++;
  }

  return errors;
}

export const zodResolver: Resolver = ((schema, schemaOptions, factoryOptions) => {
  return async (values, _context, options) => {
    const result = await schema[factoryOptions?.mode === 'sync' ? 'safeParse' : 'safeParseAsync'](
      values,
      schemaOptions,
    );

    if (result.success) {
      if (options.shouldUseNativeValidation) {
        validateFieldsNatively({}, options);
      }
      return {
        errors: {},
        values: factoryOptions?.raw ? Object.assign({}, values) : result.data,
      };
    }

    return {
      values: {},
      errors: toNestErrors(
        parseIssues(
          result.error.issues,
          !options.shouldUseNativeValidation && options.criteriaMode === 'all',
        ),
        options,
      ),
    };
  };
}) as Resolver;
